import {
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { Store } from '@blocksuite/store';
import { effect as signalEffect } from '@preact/signals-core';
import { useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import { DocsService } from '../../../../modules/doc';
import {
  getRelationRestrictedCellLabel,
  resolveRelationEndpointAccess,
} from './access-state';
import {
  type DatabaseDiscoveryUpdateDetail,
  findDatabaseByBlockId,
  getDatabaseDiscoveryKey,
  getDatabaseDiscoverySettleDelayMs,
  isBridgeInitialized,
  isDatabaseDiscoverySettling,
  setCrossDocRowDisplayCached,
} from './database-discovery-bridge';
import {
  ensureRelationRowDataLoaded,
  findDatabaseBlockModel,
  formatRelationDisplayValue,
  getDatabaseDataSource,
  getRelationRowDataLoadState,
} from './row-projection';

export const RELATION_DISPLAY_LOADING = 'loading ...';
export const RELATION_ROW_DELETED = '(deleted)';
export const RELATION_DATABASE_DELETED = '\u26A0\uFE0F DB deleted';
export const RELATION_DATABASE_RESTRICTED = 'Restricted relation';

type RelationTargetStoreHandle = {
  store: Store;
  release: () => void;
  waitForSyncReady: () => Promise<void>;
};

export type RelationTargetStoreProvider = {
  getLoadedStore: (docId: string) => Store | undefined;
  openStore: (docId: string) => RelationTargetStoreHandle | undefined;
};

export type RelationDisplayRequest = {
  targetStoreProvider: RelationTargetStoreProvider;
  rowId: string;
  targetDatabaseId: string;
  targetDocId?: string;
  targetFieldId: string;
};

export type RelationDisplayState =
  | 'loading'
  | 'ready'
  | 'missing'
  | 'restricted'
  | 'trashed';

export type RelationDisplayResult = {
  state: RelationDisplayState;
  value: string;
};

type RelationTargetContext =
  | {
      state: 'loading';
      store?: undefined;
      docId?: string;
    }
  | {
      state: 'missing';
      store?: undefined;
      docId?: undefined;
    }
  | {
      state: 'restricted';
      store?: undefined;
      docId?: string;
    }
  | {
      state: 'trashed';
      store?: undefined;
      docId?: string;
    }
  | {
      state: 'ready';
      store: Store;
      docId: string;
    };

function getRelationTargetContext({
  targetStoreProvider,
  targetDatabaseId,
  targetDocId,
}: Pick<
  RelationDisplayRequest,
  'targetStoreProvider' | 'targetDatabaseId' | 'targetDocId'
>): RelationTargetContext {
  if (!isBridgeInitialized()) {
    return { state: 'loading' };
  }

  const targetDatabase = findDatabaseByBlockId(targetDatabaseId);
  if (!targetDatabase) {
    if (isDatabaseDiscoverySettling()) {
      return { state: 'loading' };
    }
    const access = resolveRelationEndpointAccess({
      targetDatabaseId,
      targetDocId,
    });
    if (access.state === 'restricted') {
      return { state: 'restricted', docId: access.targetDocId };
    }
    return { state: 'missing' };
  }

  const access = resolveRelationEndpointAccess({
    targetDatabaseId,
    targetDocId: targetDatabase.docId,
  });
  if (access.state === 'restricted') {
    return { state: 'restricted', docId: access.targetDocId };
  }
  if (access.state === 'trashed') {
    return { state: 'trashed', docId: access.targetDocId };
  }

  const docId = targetDatabase.docId;
  const store = targetStoreProvider.getLoadedStore(docId);
  if (!store) {
    return { state: 'loading', docId };
  }

  return { state: 'ready', store, docId };
}

function matchesDatabaseDiscoveryUpdate(
  detail: DatabaseDiscoveryUpdateDetail | undefined,
  docId: string | undefined,
  databaseId: string
): boolean {
  if (!detail) return true;
  const hasScopedChanges =
    detail.affectedDatabaseKeys.length > 0 ||
    detail.affectedDatabaseIds.length > 0;
  if (!hasScopedChanges) return false;

  if (!docId) {
    return detail.affectedDatabaseIds.includes(databaseId);
  }

  const targetKey = getDatabaseDiscoveryKey({
    docId,
    blockId: databaseId,
  });
  return (
    detail.affectedDatabaseKeys.includes(targetKey) ||
    detail.affectedDatabaseIds.includes(databaseId)
  );
}

export function resolveRelationDisplayValue(
  request: RelationDisplayRequest
): RelationDisplayResult {
  const { rowId, targetDatabaseId, targetFieldId } = request;
  if (!targetDatabaseId || !targetFieldId) {
    return { state: 'missing', value: rowId.slice(0, 8) };
  }

  const target = getRelationTargetContext(request);
  if (target.state === 'loading') {
    return { state: 'loading', value: RELATION_DISPLAY_LOADING };
  }
  if (target.state === 'missing') {
    return { state: 'missing', value: RELATION_DATABASE_DELETED };
  }
  if (target.state === 'restricted') {
    return { state: 'restricted', value: getRelationRestrictedCellLabel() };
  }
  if (target.state === 'trashed') {
    return { state: 'trashed', value: RELATION_DATABASE_DELETED };
  }

  const targetModel = findDatabaseBlockModel(target.store, targetDatabaseId);
  if (!targetModel) {
    return { state: 'loading', value: RELATION_DISPLAY_LOADING };
  }
  if (!isDatabaseStorageModelReady(targetModel)) {
    return { state: 'loading', value: RELATION_DISPLAY_LOADING };
  }

  const dataSource = getDatabaseDataSource(target.store, targetDatabaseId);
  if (!dataSource) {
    return { state: 'loading', value: RELATION_DISPLAY_LOADING };
  }

  if (!dataSource.rowExists(rowId)) {
    ensureRelationRowDataLoaded(dataSource);
    if (getRelationRowDataLoadState(dataSource) === 'loading') {
      return { state: 'loading', value: RELATION_DISPLAY_LOADING };
    }
    return { state: 'missing', value: RELATION_ROW_DELETED };
  }

  const value = formatRelationDisplayValue(
    dataSource.cellValueGet(rowId, targetFieldId)
  );
  setCrossDocRowDisplayCached(
    target.docId,
    targetDatabaseId,
    rowId,
    value || rowId
  );
  return { state: 'ready', value: value || rowId };
}

export function useRelationDisplayValue(
  request: RelationDisplayRequest
): string {
  const {
    targetStoreProvider,
    rowId,
    targetDatabaseId,
    targetDocId,
    targetFieldId,
  } = request;
  const stableRequest = useMemo(
    () => ({
      targetStoreProvider,
      rowId,
      targetDatabaseId,
      targetDocId,
      targetFieldId,
    }),
    [targetStoreProvider, rowId, targetDatabaseId, targetDocId, targetFieldId]
  );
  const [displayValue, setDisplayValue] = useState(
    () => resolveRelationDisplayValue(stableRequest).value
  );

  useEffect(() => {
    let updateFrame: number | null = null;
    const update = () => {
      setDisplayValue(resolveRelationDisplayValue(stableRequest).value);
    };
    const scheduleUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = requestAnimationFrame(() => {
        updateFrame = null;
        update();
      });
    };

    const cleanupTasks: Array<() => void> = [];
    const target = getRelationTargetContext(stableRequest);
    const targetDocIdForDiscovery = target.docId;

    if (target.state === 'loading' && target.docId) {
      try {
        const opened = targetStoreProvider.openStore(target.docId);
        if (opened) {
          cleanupTasks.push(opened.release);
          opened.waitForSyncReady().then(
            () => scheduleUpdate(),
            () => scheduleUpdate()
          );
        }
      } catch {
        scheduleUpdate();
      }
    } else if (target.state === 'loading' && targetDatabaseId) {
      const timeout = window.setTimeout(
        scheduleUpdate,
        getDatabaseDiscoverySettleDelayMs()
      );
      cleanupTasks.push(() => window.clearTimeout(timeout));
    }

    update();

    const handleDatabaseDiscoveryUpdate = (
      event: Event | CustomEvent<DatabaseDiscoveryUpdateDetail>
    ) => {
      if (
        matchesDatabaseDiscoveryUpdate(
          (event as CustomEvent<DatabaseDiscoveryUpdateDetail>).detail,
          targetDocIdForDiscovery,
          targetDatabaseId
        )
      ) {
        scheduleUpdate();
      }
    };
    window.addEventListener(
      'database-discovery-updated',
      handleDatabaseDiscoveryUpdate
    );
    cleanupTasks.push(() =>
      window.removeEventListener(
        'database-discovery-updated',
        handleDatabaseDiscoveryUpdate
      )
    );

    if (target.state === 'ready' && targetDatabaseId) {
      const targetModel = findDatabaseBlockModel(
        target.store,
        targetDatabaseId
      );
      if (!targetModel) {
        const blockUpdated$ = (
          target.store as Store & {
            slots?: {
              blockUpdated?: {
                subscribe: (
                  callback: (event: {
                    type: string;
                    id: string;
                    flavour: string;
                    isLocal: boolean;
                  }) => void
                ) => {
                  unsubscribe: () => void;
                };
              };
            };
          }
        ).slots?.blockUpdated;
        const disposable = blockUpdated$?.subscribe(() => {
          scheduleUpdate();
        });
        if (disposable) {
          cleanupTasks.push(() => disposable.unsubscribe());
        }
      }
      if (targetModel && !isDatabaseStorageModelReady(targetModel)) {
        let readyHandled = false;
        const dispose = observeDatabaseStorageModelReady(targetModel, () => {
          if (readyHandled) return;
          readyHandled = true;
          scheduleUpdate();
          queueMicrotask(() => dispose());
        });
        cleanupTasks.push(() => {
          readyHandled = true;
          dispose();
        });
      }

      const dataSource = getDatabaseDataSource(target.store, targetDatabaseId);
      if (dataSource && targetFieldId) {
        if (!dataSource.rowExists(rowId)) {
          ensureRelationRowDataLoaded(dataSource);
        }
        const valueSignal = dataSource.cellValueGet$(rowId, targetFieldId);
        const dispose = signalEffect(() => {
          const rowDataLoadState = getRelationRowDataLoadState(dataSource);
          const value = dataSource.rowExists(rowId)
            ? valueSignal.value
            : undefined;
          void value;
          scheduleUpdate();
          if (rowDataLoadState === 'loading' && !dataSource.rowExists(rowId)) {
            ensureRelationRowDataLoaded(dataSource);
          }
        });
        cleanupTasks.push(dispose);
      }
    }

    scheduleUpdate();

    return () => {
      for (const cleanup of cleanupTasks) cleanup();
      if (updateFrame !== null) {
        cancelAnimationFrame(updateFrame);
      }
    };
  }, [
    stableRequest,
    rowId,
    targetDatabaseId,
    targetFieldId,
    targetStoreProvider,
  ]);

  return displayValue;
}

export function useRelationTargetStoreProvider(): RelationTargetStoreProvider {
  const docsService = useService(DocsService);

  return useMemo(
    () => ({
      getLoadedStore: (docId: string) =>
        docsService.loaded(docId)?.doc.blockSuiteDoc,
      openStore: (docId: string) => {
        const { doc, release } = docsService.open(docId);
        return {
          store: doc.blockSuiteDoc,
          release,
          waitForSyncReady: () => doc.waitForSyncReady(),
        };
      },
    }),
    [docsService]
  );
}
